import { createServer, Model, Response } from 'miragejs'

import { ENV, isDevelopment, USE_MINIMAL_MOCK } from '~constants'
import {
  nfts,
  offers,
  pointsHistory,
  successResponseMockStructure,
} from '~mocks'
import { generateID } from '~utils'

const passthroughUrls = [
  'https://*.rocketdemo.net/**',
  'https://*.ngrok-free.app/**',
  'https://*.rpay.inc/**',
  'http://localhost:3015/**',
  // Local docker / compose loyalty BE (shopper app API_URL)
  'http://localhost:60515/**',
  'http://127.0.0.1:60515/**',
  'https://e451b99bd939.ngrok-free.app/**',
]

const getApiOriginPassthrough = (): string | undefined => {
  if (!ENV.API_URL) return undefined
  try {
    return `${new URL(ENV.API_URL).origin}/**`
  } catch {
    return undefined
  }
}

export const startMockedServer = (): void => {
  if (window.server) {
    // remove return if you want to work with this file
    if (isDevelopment) return
    server.shutdown()
  }

  window.server = createServer({
    models: {
      filters: Model,
      history: Model,
      lastUserId: Model,
      nft: Model,
      offer: Model,
      user: Model,
    },
    urlPrefix: ENV.API_URL,
    timing: 400,
    routes() {
      this.passthrough('https://qa-loyalty.rocketdemo.net/**')
      this.passthrough('https://loyalty.rpay.inc/**')
      this.passthrough('https://loyalty-sandbox.rpay.inc/**')
      this.passthrough('https://preprod-loyalty.rocketdemo.net/api/**')
      this.passthrough('http://localhost:3019/**')
      this.passthrough('https://maps.googleapis.com/**')

      passthroughUrls.forEach((url) => this.passthrough(url))
      const apiOrigin = getApiOriginPassthrough()
      if (apiOrigin) {
        this.passthrough(apiOrigin)
      }
      // Let real BE handle any route Mirage does not mock (e.g. /invite/resolve)
      this.passthrough()

      // HISTORY
      this.get(`${shouldBlockMockedServer()}/history`, () => {
        const pointsHistory = server.db.histories
        return successResponseMockStructure({ pointsHistory })
      })

      // OFFERS
      this.get(`/${shouldBlockMockedServer(USE_MINIMAL_MOCK)}offers`, (schema) => {
        const offers = schema.all('offer').models
        return successResponseMockStructure({ offers })
      })

      this.get(
        `/${shouldBlockMockedServer(USE_MINIMAL_MOCK)}offers/:offerId`,
        (schema, request) => {
          const offerId = request.params.offerId
          // const offer = server.db.offers.findBy({ id: offerId })
          const offer = schema.where('offer', { id: offerId }).models[0]

          return successResponseMockStructure({ offer })
        }
      )

      this.post(`/${shouldBlockMockedServer(USE_MINIMAL_MOCK)}buy-offer`, (_, request) => {
        const { offerId } = JSON.parse(request.requestBody)
        const offer = server.db.offers.findBy({ id: offerId })

        const lastUserId = server.db.lastUserIds[0]?.id

        let currentUser = undefined
        if (lastUserId) {
          currentUser = server.db.users.findBy({ id: lastUserId })
        }

        if (!currentUser) {
          return new Response(
            500,
            {},
            {
              ok: false,
              statusCode: 401,
              data: {},
              message: 'ERROR.USER.NOT.FOUND',
            }
          )
        }

        if (!offer) {
          return new Response(
            500,
            {},
            {
              ok: false,
              statusCode: 404,
              data: {},
              message: 'ERROR.NOT.FOUND',
            }
          )
        }

        if (offer.buyStatus === 1) {
          return new Response(
            500,
            {},
            {
              ok: false,
              statusCode: 409,
              data: {},
              message: 'ERROR.ALREADY.USED',
            }
          )
        }
        server.db.offers.update({ id: offerId }, { buyStatus: 1, discountCode: generateID() })

        return successResponseMockStructure({
          ...currentUser,
          points: String(currentUser.points - offer.points),
        })
      })

      // NFT
      this.get(`${shouldBlockMockedServer()}/nfts`, (schema) => {
        const nfts = schema.all('nft').models
        return successResponseMockStructure({ nfts })
      })

      this.get(`${shouldBlockMockedServer()}/nfts/:nftId`, (schema, request) => {
        const nftId = request.params.nftId
        const nftDetails = schema.where('nft', { id: nftId }).models[0]

        return successResponseMockStructure({ nftDetails })
      })

      // SIGN IN
      this.post(`/${shouldBlockMockedServer(true)}sign-in`, () => {
        return successResponseMockStructure({})
      })



      // USER
      this.get(`/${shouldBlockMockedServer(true)}me`, async () => {
        const userId = server.db.lastUserIds[0]?.id
        const user = server.db.users.findBy({ id: userId })

        return successResponseMockStructure({ user })
      })

      this.post(`/${shouldBlockMockedServer(true)}me`, (_, request) => {
        const params = JSON.parse(request.requestBody)

        if (Object.keys(params).length === 0) {
          return new Response(
            500,
            {},
            {
              ok: false,
              statusCode: 400,
              data: {},
              message: 'ERROR.PARAMETERS.NOT.PROVIDED',
            }
          )
        }

        const lastUserId = server.db.lastUserIds[0]?.id
        server.db.users.update({ id: lastUserId }, { ...params })

        return successResponseMockStructure({})
      })

      this.post(`/${shouldBlockMockedServer()}createUser`, (_, request) => {
        const params = JSON.parse(request.requestBody)

        if (Object?.keys(params)?.length === 0) {
          return new Response(
            500,
            {},
            {
              ok: false,
              statusCode: 400,
              data: {},
              message: 'ERROR.PARAMETERS.NOT.PROVIDED',
            }
          )
        }

        server.db.lastUserIds.remove()
        server.db.lastUserIds.insert({ id: params.id })

        const user = server.db.users.findBy({ id: params.id })

        if (!user && params.id) {
          server.db.users.insert(params)
        }
        return successResponseMockStructure({})
      })
    },
    seeds(server) {
      // @ts-expect-error: type error
      pointsHistory.map((el) => server.create('history', el))
      nfts.map((nft) => server.create('nft', nft))
      offers.map((offer) => server.create('offer', offer))
      server.create('lastUserId', { id: 'initial' })
    },
  })
}

// do not pass should block = true if BE unavailable
const shouldBlockMockedServer = (shouldBlock = false) => {
  return shouldBlock ? '_' : ''
}
